import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    MapPin,
    Gauge,
    Star,
    BadgeCheck,
    MessageCircle,
    Heart,
    HeartOff,
    Send,
    ChevronLeft,
    ChevronRight,
    Zap,
} from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError, absUrl } from "@/lib/api";
import { formatPrice, formatMileage, timeAgo } from "@/lib/constants";
import ListingCard from "@/components/ListingCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ReportButton from "@/components/ReportButton";
import Seo, { vehicleListingSchema } from "@/components/Seo";
import TradeProposalDialog from "@/components/TradeProposalDialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";

export default function ListingDetail() {
    const { id } = useParams();
    const [listing, setListing] = useState(null);
    const [idx, setIdx] = useState(0);
    const [saved, setSaved] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [contactMode, setContactMode] = useState("message");
    const [contactBody, setContactBody] = useState("");
    const [tradeOpen, setTradeOpen] = useState(false);
    const [markSoldOpen, setMarkSoldOpen] = useState(false);
    const [soldPrice, setSoldPrice] = useState("");
    const [markingSold, setMarkingSold] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/listings/${id}`);
                setListing(data);
                setIdx(0);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!user || !listing) return;
        (async () => {
            try {
                const { data } = await api.get("/favorites");
                setSaved(data.listings.some((l) => l.listing_id === listing.listing_id));
            } catch {}
        })();
    }, [user, listing]);

    async function toggleSave() {
        if (!user) return navigate("/login");
        try {
            if (saved) {
                await api.delete(`/favorites/listing/${listing.listing_id}`);
                setSaved(false);
            } else {
                await api.post(`/favorites/listing/${listing.listing_id}`);
                setSaved(true);
                toast.success("Saved to your watchlist");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    function openContact(mode) {
        if (!user) return navigate("/login");
        setContactMode(mode);
        setContactBody(
            mode === "trade"
                ? `Interested in a trade for your ${listing.year} ${listing.model_name}. Here's what I've got...`
                : mode === "offer"
                  ? `Would you consider $${(listing.price - 2000).toLocaleString()} for this ${listing.model_name}?`
                  : `Hi, is your ${listing.year} ${listing.model_name} still available?`,
        );
        setContactOpen(true);
    }

    async function sendContact() {
        if (!contactBody.trim()) return;
        try {
            const { data } = await api.post("/messages/conversations", {
                recipient_id: listing.owner_id,
                listing_id: listing.listing_id,
                initial_message: contactBody.trim(),
            });
            setContactOpen(false);
            toast.success("Message sent");
            navigate(`/messages/${data.conversation_id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    if (!listing) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-onyx-900">
                <div className="w-10 h-10 border-2 border-onyx-600 border-t-hellcat rounded-full animate-spin" />
            </div>
        );
    }

    const photos = listing.photos?.length ? listing.photos : [];
    const marketAvg = listing.market_average?.average;
    const spread = marketAvg ? Math.round(((listing.price - marketAvg) / marketAvg) * 100) : null;

    const seoTitle = `${listing.year} ${listing.make} ${listing.model_name} ${listing.trim} for sale — $${listing.price.toLocaleString()} · ${listing.mileage.toLocaleString()} mi · ${listing.location}`;
    const seoDesc = `${listing.year} ${listing.make} ${listing.model_name} ${listing.trim} (${listing.engine}, ${listing.drivetrain}, ${listing.transmission}) for sale on SRT Finder — the exclusive Mopar performance marketplace. ${listing.mileage.toLocaleString()} miles, ${listing.mods_level.toLowerCase()}, located in ${listing.location}. ${(listing.description || "").slice(0, 160)}`;

    return (
        <div className="min-h-screen bg-onyx-900">
            <Seo
                title={seoTitle}
                description={seoDesc}
                canonicalPath={`/listings/${listing.listing_id}`}
                image={
                    photos[0]
                        ? photos[0].startsWith("http")
                            ? photos[0]
                            : `https://srtfinder.com${photos[0]}`
                        : undefined
                }
                imageAlt={`${listing.year} ${listing.make} ${listing.model_name} ${listing.trim} for sale on SRT Finder`}
                type="product"
                jsonLd={vehicleListingSchema(listing)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Marketplace", path: "/marketplace" },
                    {
                        name: `${listing.year} ${listing.model_name}`,
                        path: `/listings/${listing.listing_id}`,
                    },
                ]}
            />
            {/* Top nav */}
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="flex-1 truncate">
                        <div className="text-xs text-onyx-500">Listing</div>
                        <div className="headline text-base truncate">
                            {listing.year} {listing.model_name} {listing.trim}
                        </div>
                    </div>
                    <button
                        onClick={toggleSave}
                        data-testid="save-listing-btn"
                        className="btn-ghost rounded-sm px-3 py-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest"
                    >
                        {saved ? (
                            <>
                                <HeartOff className="w-4 h-4" /> Saved
                            </>
                        ) : (
                            <>
                                <Heart className="w-4 h-4" /> Save
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-4">
                {/* Gallery */}
                <div className="relative surface rounded-md overflow-hidden">
                    <div className="relative w-full listing-image" style={{ aspectRatio: "16/10" }}>
                        {photos.length ? (
                            <img
                                key={idx}
                                src={absUrl(photos[idx])}
                                className="w-full h-full object-cover"
                                alt="listing"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                                No Photos
                            </div>
                        )}
                        {photos.length > 1 && (
                            <>
                                <button
                                    onClick={() => setIdx((idx - 1 + photos.length) % photos.length)}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                                    style={{ background: "rgba(10,10,10,0.7)" }}
                                    data-testid="gallery-prev"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setIdx((idx + 1) % photos.length)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                                    style={{ background: "rgba(10,10,10,0.7)" }}
                                    data-testid="gallery-next"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                    {photos.map((_, i) => (
                                        <span
                                            key={i}
                                            className="w-1.5 h-1.5 rounded-full"
                                            style={{
                                                background:
                                                    i === idx ? "#E10600" : "rgba(255,255,255,0.4)",
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    {photos.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto no-scrollbar p-2">
                            {photos.map((p, i) => (
                                <button
                                    key={p + i}
                                    onClick={() => setIdx(i)}
                                    className={`shrink-0 w-20 h-14 rounded-sm overflow-hidden border-2 ${
                                        i === idx ? "border-hellcat" : "border-transparent"
                                    }`}
                                >
                                    <img
                                        src={absUrl(p)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Title + Price */}
                <div className="mt-5">
                    <div className="label-caps text-hellcat">{listing.trim}</div>
                    <h1 className="headline text-3xl sm:text-4xl mt-1">
                        {listing.year} {listing.make} {listing.model_name}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                        <div className="headline text-3xl text-white">{formatPrice(listing.price)}</div>
                        {spread != null && listing.market_average.count > 0 && (
                            <div
                                className={`label-caps rounded-sm px-2 py-1 ${
                                    spread <= 0
                                        ? "bg-green-950 text-green-400"
                                        : spread < 8
                                          ? "bg-onyx-800 text-onyx-500"
                                          : "bg-hellcat-dim text-white"
                                }`}
                                style={
                                    spread <= 0
                                        ? { background: "rgba(22,163,74,0.15)", color: "#16A34A" }
                                        : {}
                                }
                                data-testid="market-badge"
                            >
                                {spread === 0
                                    ? "At market"
                                    : spread < 0
                                      ? `${Math.abs(spread)}% below market`
                                      : `${spread}% above market`}
                            </div>
                        )}
                    </div>
                    {listing.market_average?.count > 0 && (
                        <div className="text-xs text-onyx-500 mt-1">
                            Based on {listing.market_average.count} similar recent{" "}
                            {listing.model_name} listings on SRT Finder (avg{" "}
                            {formatPrice(listing.market_average.average)}).
                        </div>
                    )}
                </div>

                {/* Quick facts */}
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Fact label="Mileage" value={formatMileage(listing.mileage)} icon={Gauge} />
                    <Fact label="Location" value={listing.location} icon={MapPin} />
                    <Fact label="Engine" value={listing.engine} icon={Zap} />
                    <Fact label="Drivetrain" value={listing.drivetrain} />
                    <Fact label="Trans" value={listing.transmission} />
                    <Fact label="Mods" value={listing.mods_level} />
                    <Fact
                        label="Seller"
                        value={listing.seller_type === "dealer" ? "Dealer" : "Private"}
                    />
                    <Fact label="Posted" value={timeAgo(listing.created_at)} />
                </div>

                {/* Badges */}
                <div className="mt-4 flex flex-wrap gap-2" data-testid="listing-badges">
                    <span className="chip" style={{
                        background: listing.seller_type === "dealer" ? "rgba(59,130,246,0.14)" : "rgba(74,222,128,0.14)",
                        borderColor: listing.seller_type === "dealer" ? "rgba(59,130,246,0.4)" : "rgba(74,222,128,0.4)",
                        color: listing.seller_type === "dealer" ? "#93c5fd" : "#86efac",
                    }} data-testid="badge-seller-type">
                        {listing.seller_type === "dealer" ? "DEALER" : "PRIVATE SELLER"}
                    </span>
                    <span className="chip uppercase text-[10px]" data-testid="badge-mods">
                        {listing.mods_level}
                    </span>
                    {listing.photo_verified && (
                        <span className="chip" style={{
                            background: "rgba(225,6,0,0.14)",
                            borderColor: "rgba(225,6,0,0.4)",
                            color: "#FF6E68",
                        }} data-testid="badge-photo-verified">
                            <BadgeCheck className="w-3 h-3" /> PHOTO VERIFIED
                        </span>
                    )}
                    {listing.under_market_pct >= 8 && (
                        <span className="chip" style={{
                            background: "rgba(74,222,128,0.14)",
                            borderColor: "rgba(74,222,128,0.4)",
                            color: "#86efac",
                        }} data-testid="badge-under-market">
                            ↓ {listing.under_market_pct}% UNDER MKT
                        </span>
                    )}
                    {listing.seller?.verified_seller && (
                        <span className="chip" style={{
                            background: "rgba(225,6,0,0.14)",
                            borderColor: "rgba(225,6,0,0.4)",
                            color: "#FF6E68",
                        }} data-testid="badge-verified-seller">
                            <BadgeCheck className="w-3 h-3" /> VERIFIED SELLER
                        </span>
                    )}
                </div>

                {/* Actions */}
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <button
                        onClick={() => openContact("message")}
                        data-testid="btn-message-seller"
                        className="btn-red rounded-sm py-3 flex items-center justify-center gap-2"
                    >
                        <MessageCircle className="w-4 h-4" /> Message Seller
                    </button>
                    <button
                        onClick={() => openContact("offer")}
                        data-testid="btn-make-offer"
                        className="btn-ghost rounded-sm py-3 font-bold uppercase tracking-widest text-sm"
                    >
                        Make Offer
                    </button>
                    <button
                        onClick={() => {
                            if (!user) return navigate("/login");
                            setTradeOpen(true);
                        }}
                        data-testid="btn-propose-trade"
                        className="btn-ghost rounded-sm py-3 font-bold uppercase tracking-widest text-sm"
                    >
                        Propose Trade
                    </button>
                </div>

                {/* Owner actions — mark-sold */}
                {user && listing.owner_id === user.user_id && listing.status !== "sold" && (
                    <div className="mt-3">
                        <button
                            type="button"
                            data-testid="btn-mark-sold"
                            onClick={() => {
                                setSoldPrice(String(listing.price));
                                setMarkSoldOpen(true);
                            }}
                            className="btn-red rounded-sm px-4 py-2 text-sm font-bold uppercase tracking-widest w-full"
                        >
                            Mark as sold
                        </button>
                    </div>
                )}

                {listing.status === "sold" && (
                    <div className="mt-3 text-center py-2 rounded-sm"
                         data-testid="sold-banner"
                         style={{ background: "rgba(225,6,0,0.14)", border: "1px solid rgba(225,6,0,0.4)" }}>
                        <span className="text-hellcat font-bold uppercase tracking-widest text-xs">
                            Sold {listing.sold_at ? `on ${listing.sold_at.slice(0, 10)}` : ""}
                            {listing.sold_price ? ` for $${listing.sold_price.toLocaleString()}` : ""}
                        </span>
                    </div>
                )}

                <TradeProposalDialog
                    open={tradeOpen}
                    onOpenChange={setTradeOpen}
                    listing={listing}
                    onCreated={(proposal) => {
                        if (proposal?.conversation_id) navigate(`/messages/${proposal.conversation_id}`);
                    }}
                />

                <Dialog open={markSoldOpen} onOpenChange={setMarkSoldOpen}>
                    <DialogContent className="max-w-sm surface" data-testid="mark-sold-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-white">Mark as sold</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <label className="block">
                                <span className="label-caps mb-1 block">Final sold price</span>
                                <input
                                    type="number"
                                    value={soldPrice}
                                    onChange={(e) => setSoldPrice(e.target.value)}
                                    data-testid="mark-sold-price-input"
                                    min={1}
                                    className="w-full px-3 py-2.5 rounded-md bg-onyx-900 border border-white/10 text-sm text-white"
                                />
                                <p className="text-[11px] text-onyx-500 mt-1">
                                    This locks the listing, moves it to Recently Sold, and stops all buyer messages.
                                </p>
                            </label>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    data-testid="mark-sold-cancel"
                                    onClick={() => setMarkSoldOpen(false)}
                                    className="btn-ghost px-4 py-2 text-sm font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={markingSold}
                                    data-testid="mark-sold-confirm"
                                    onClick={async () => {
                                        setMarkingSold(true);
                                        try {
                                            const { data } = await api.post(
                                                `/listings/${listing.listing_id}/mark-sold`,
                                                { sold_price: soldPrice ? Number(soldPrice) : undefined },
                                            );
                                            setListing(data);
                                            setMarkSoldOpen(false);
                                            toast.success("Marked as sold");
                                        } catch (e) {
                                            toast.error(formatApiError(e));
                                        } finally {
                                            setMarkingSold(false);
                                        }
                                    }}
                                    className="btn-red px-5 py-2 text-sm font-bold"
                                >
                                    {markingSold ? "Saving…" : "Confirm"}
                                </button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Description + Mods */}
                <section className="mt-8">
                    <div className="label-caps mb-2">Description</div>
                    <p className="text-sm leading-relaxed text-onyx-300 whitespace-pre-wrap">
                        {listing.description || "No description provided."}
                    </p>
                </section>

                {listing.mods?.length > 0 && (
                    <section className="mt-6">
                        <div className="label-caps mb-2">Mods list</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {listing.mods.map((m, i) => (
                                <li
                                    key={i}
                                    className="surface rounded-sm px-3 py-2 text-sm flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-hellcat" />
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Seller card */}
                {listing.seller && (
                    <section className="mt-8 surface rounded-md p-5">
                        <Link
                            to={`/users/${listing.seller.user_id}`}
                            className="flex items-center gap-4"
                            data-testid="seller-card-link"
                        >
                            <div className="w-14 h-14 rounded-full overflow-hidden surface-elevated">
                                {listing.seller.avatar_url ? (
                                    <img
                                        src={absUrl(listing.seller.avatar_url)}
                                        alt="seller"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                                        {listing.seller.name?.[0]}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="headline text-lg flex items-center gap-2">
                                    {listing.seller.name}
                                    {listing.seller.verified_seller && (
                                        <BadgeCheck className="w-4 h-4 text-hellcat" />
                                    )}
                                </div>
                                <div className="text-xs text-onyx-500 flex items-center gap-3 mt-0.5">
                                    {listing.seller.location && (
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {listing.seller.location}
                                        </span>
                                    )}
                                    {listing.seller.seller_rating > 0 && (
                                        <span className="flex items-center gap-1">
                                            <Star className="w-3 h-3 fill-hellcat text-hellcat" />
                                            {listing.seller.seller_rating.toFixed(1)} · {listing.seller.seller_rating_count} sales
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Link>
                    </section>
                )}

                {/* Similar listings */}
                {listing.similar?.length > 0 && (
                    <section className="mt-10">
                        <div className="label-caps mb-3">Similar listings</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {listing.similar.slice(0, 3).map((l) => (
                                <ListingCard listing={l} key={l.listing_id} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Report */}
                <div className="mt-10 flex justify-end">
                    <ReportButton
                        targetType="listing"
                        targetId={listing.listing_id}
                        testid="report-listing-btn"
                    />
                </div>
            </div>

            {/* Contact dialog */}
            <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogContent
                    className="surface border-onyx-600 rounded-md max-w-md"
                    data-testid="contact-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="headline text-2xl">
                            {contactMode === "trade"
                                ? "Trade proposal"
                                : contactMode === "offer"
                                  ? "Make an offer"
                                  : "Message seller"}
                        </DialogTitle>
                    </DialogHeader>
                    <textarea
                        value={contactBody}
                        onChange={(e) => setContactBody(e.target.value)}
                        className="w-full h-32 bg-onyx-700 border border-onyx-600 rounded-sm p-3 text-sm text-white focus:outline-none focus:border-hellcat"
                        data-testid="contact-body"
                    />
                    <DialogFooter>
                        <button
                            onClick={sendContact}
                            data-testid="contact-send"
                            className="btn-red rounded-sm px-4 py-2.5 inline-flex items-center gap-2"
                        >
                            <Send className="w-4 h-4" /> Send
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Fact({ label, value, icon: Icon }) {
    return (
        <div className="surface rounded-sm p-3">
            <div className="label-caps text-[10px]">{label}</div>
            <div className="mt-1 text-sm text-white flex items-center gap-1.5 truncate">
                {Icon && <Icon className="w-3.5 h-3.5 text-onyx-500 shrink-0" />}
                <span className="truncate">{value}</span>
            </div>
        </div>
    );
}
